import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Shield, LayoutDashboard, BookOpen, Settings, Clock, Calendar } from "lucide-react";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import UserProfileDialog from "./UserProfileDialog";

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const time = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const date = now.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' });
  return (
    <div className="flex items-center gap-3 text-slate-500">
      <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /><span className="text-sm font-medium tabular-nums">{time}</span></div>
      <div className="hidden md:flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /><span className="text-sm font-medium">{date}</span></div>
    </div>
  );
}

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);

  return (
    <>
    <header className="w-full bg-rb-surface sticky top-0 z-50 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between gap-6">
        {/* Left: Reynolds & Bauhm Logo — stays on same page */}
        <a href="/" className="flex items-center shrink-0 gap-3 hover:opacity-80 transition-opacity">
          <img
            src="/logo.jpg"
            alt="Reynolds & Bauhm"
            className="h-11 w-auto"
          />
        </a>

        {/* Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          <a
            href="https://reynoldsbauhm.co.uk/industries/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-[15px] text-slate-400 hover:text-rb-active transition-colors font-medium flex items-center gap-1"
          >
            Industries
            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <a
            href="https://reynoldsbauhm.co.uk/equipment-systems/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-[15px] text-slate-400 hover:text-rb-active transition-colors font-medium flex items-center gap-1"
          >
            Equipment & Systems
            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <a
            href="https://reynoldsbauhm.co.uk/services/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-[15px] text-slate-400 hover:text-rb-active transition-colors font-medium flex items-center gap-1"
          >
            Our Engineering Services
            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <button
            onClick={() => {
              if (window.location.pathname !== '/') {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('sample-data-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              } else {
                document.getElementById('sample-data-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="px-3 py-2 text-[15px] text-slate-400 hover:text-rb-active transition-colors font-medium flex items-center gap-1"
          >
            Sample Data
          </button>
        </nav>

        {/* Right: Instructions + Clock + Auth + CTA */}
        <div className="flex items-center gap-4">
          {/* Live Clock */}
          <LiveClock />
          {/* How to Use Button */}
          <button
            onClick={() => navigate("/instructions")}
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-rb-active hover:text-rb-brand hover:bg-rb-elevated border border-transparent hover:border-rb-brand/20 transition-all"
            title="How to use this calculator"
          >
            <BookOpen className="w-4 h-4" />
            How to Use
          </button>

          {/* Auth */}
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-rb-elevated transition-colors">
                <div className="w-8 h-8 rounded-full bg-rb-brand text-slate-200 flex items-center justify-center text-sm font-bold">
                    {(user.name || user.username || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-slate-400">
                    {user.name || user.username || "User"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {user.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setShowProfile(true)}
                  className="cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/")}
                  className="cursor-pointer"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Calculator
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/login")}
              className="gap-2"
            >
              <User className="w-4 h-4" />
              Sign In
            </Button>
          )}

          {/* CTA Button */}
          <a
            href="https://reynoldsbauhm.co.uk/contact/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex px-5 py-2.5 bg-rb-active text-white text-[15px] font-medium rounded hover:bg-[#152d4a] transition-colors"
          >
            Speak to Our Engineers
          </a>
        </div>
      </div>
    </header>
    <UserProfileDialog isOpen={showProfile} onClose={() => setShowProfile(false)} />
    </>
  );
}